import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getForecastedActivitiesList,
  saveForecastedActivityRow,
  deleteForecastedActivityRow,
  getOutstandingItemsList,
  saveOutstandingItemRow,
  deleteOutstandingItemRow,
  getForecastingSummary,
  saveForecastingSummary,
  type ForecastedActivityRow,
  type ForecastRiskLevel,
  type OutstandingItemRow,
  type ItemPriority,
  type NextShiftRiskAssessment,
  type ResourceAdequacy,
  type ForecastingSummaryRow,
} from '@/services/db/dsrEntry';
import { FieldLabel, FieldError, ChipGroup, MultiChipGroup } from './_shared';
import { ACTIVITY_RISK_TAGS } from '@/constants/activityRiskTags';

const RISK_LEVEL_OPTIONS: { value: ForecastRiskLevel; label: string; color: string }[] = [
  { value: 'high', label: 'High', color: '#d87820' },
  { value: 'medium', label: 'Medium', color: '#00b4b4' },
  { value: 'low', label: 'Low', color: '#3ba870' },
];

const PRIORITY_OPTIONS: { value: ItemPriority; label: string; color: string }[] = [
  { value: 'high', label: 'High', color: '#d87820' },
  { value: 'medium', label: 'Medium', color: '#00b4b4' },
  { value: 'low', label: 'Low', color: '#3ba870' },
];

const RISK_ASSESSMENT_OPTIONS: { value: NextShiftRiskAssessment; label: string }[] = [
  { value: 'low_risk', label: 'Low Risk' },
  { value: 'elevated_risk', label: 'Elevated Risk' },
  { value: 'high_risk', label: 'High Risk' },
  { value: 'critical_risk', label: 'Critical Risk' },
];

const RESOURCE_ADEQUACY_OPTIONS: { value: ResourceAdequacy; label: string }[] = [
  { value: 'fully_adequate', label: 'Fully Adequate' },
  { value: 'minor_gaps', label: 'Minor Gaps' },
  { value: 'significant_gaps', label: 'Significant Gaps' },
  { value: 'inadequate', label: 'Inadequate' },
];

const forecastedActivitySchema = z
  .object({
    description: z.string().min(1, 'Description is required').max(500),
    anticipated_risk: z.string().min(1, 'Risk level is required'),
    risk_notes: z.string().max(300).optional(),
    activity_tags: z.array(z.string()),
    is_permits_required: z.boolean(),
    estimated_duration: z.string().optional(),
    notes: z.string().max(300).optional(),
  })
  .refine(
    (data) =>
      data.anticipated_risk !== 'high' ||
      (data.risk_notes && data.risk_notes.trim().length > 0),
    {
      message: 'Risk notes are required for High risk activities',
      path: ['risk_notes'],
    },
  );

type ActivityFormValues = z.infer<typeof forecastedActivitySchema>;

const EMPTY_ACTIVITY: ActivityFormValues = {
  description: '',
  anticipated_risk: '',
  risk_notes: '',
  activity_tags: [],
  is_permits_required: false,
  estimated_duration: '',
  notes: '',
};

const outstandingItemSchema = z.object({
  description: z.string().min(1, 'Description is required').max(300),
  priority: z.string().min(1, 'Priority is required'),
});

type ItemFormValues = z.infer<typeof outstandingItemSchema>;

const EMPTY_ITEM: ItemFormValues = { description: '', priority: '' };

const summarySchema = z
  .object({
    overall_risk_assessment: z.string().min(1, 'Required'),
    risk_comments: z.string().max(300).optional(),
    resource_adequacy: z.string().min(1, 'Required'),
    resource_comments: z.string().max(300).optional(),
    next_shift_narrative: z.string().max(500).optional(),
  })
  .refine(
    (data) =>
      data.overall_risk_assessment === 'low_risk' ||
      (data.risk_comments && data.risk_comments.trim().length > 0),
    {
      message: 'Risk comments are required for elevated/high/critical risk',
      path: ['risk_comments'],
    },
  );

type SummaryFormValues = z.infer<typeof summarySchema>;

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function ForecastingForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  const [activityMode, setActivityMode] = useState<
    'closed' | 'adding' | { editing: ForecastedActivityRow }
  >('closed');
  const [itemMode, setItemMode] = useState<
    'closed' | 'adding' | { editing: OutstandingItemRow }
  >('closed');

  const { data: forecasted = [], isLoading: loadingForecasted } = useQuery({
    queryKey: ['entry_forecasted_activities', dsrId],
    queryFn: () => getForecastedActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });
  const { data: outstanding = [], isLoading: loadingItems } = useQuery({
    queryKey: ['entry_outstanding_items', dsrId],
    queryFn: () => getOutstandingItemsList(dsrId),
    staleTime: 30 * 1000,
  });
  const { data: summary } = useQuery({
    queryKey: ['entry_forecasting_summary', dsrId],
    queryFn: () => getForecastingSummary(dsrId),
    staleTime: 30 * 1000,
  });

  const onActivitySaved = () => {
    setActivityMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_forecasted_activities', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onActivityDelete = async (id: string) => {
    if (!confirm('Remove this forecasted activity?')) return;
    try {
      await deleteForecastedActivityRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_forecasted_activities', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete activity');
    }
  };

  const onItemSaved = () => {
    setItemMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_outstanding_items', dsrId] });
  };

  const onItemDelete = async (id: string) => {
    if (!confirm('Remove this outstanding item?')) return;
    try {
      await deleteOutstandingItemRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_outstanding_items', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete item');
    }
  };

  if (loadingForecasted || loadingItems) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold">Activity Forecasting</h2>
        <p className="text-xs text-muted-foreground">
          Look ahead to the next shift: planned activities, outstanding items, and risk
          assessment.
        </p>
      </div>

      {/* ── Forecasted Activities ── */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Next-Shift Activities
        </h3>

        {forecasted.length === 0 && activityMode === 'closed' && (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No forecasted activities yet.
            </CardContent>
          </Card>
        )}

        {forecasted.map((entry) => (
          <ForecastedActivityCard
            key={entry.id}
            entry={entry}
            onEdit={() => setActivityMode({ editing: entry })}
            onDelete={() => onActivityDelete(entry.id)}
            isReadOnly={isReadOnly}
          />
        ))}

        {!isReadOnly && activityMode !== 'closed' && tenantId && (
          <ForecastedActivityEntryForm
            dsrId={dsrId}
            tenantId={tenantId}
            editing={activityMode !== 'adding' ? activityMode.editing : undefined}
            onSaved={onActivitySaved}
            onCancel={() => setActivityMode('closed')}
          />
        )}

        {!isReadOnly && activityMode === 'closed' && (
          <Button
            variant="outline"
            className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
            onClick={() => setActivityMode('adding')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add Forecasted Activity
          </Button>
        )}
      </section>

      {/* ── Outstanding Items ── */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Outstanding Items
        </h3>

        {outstanding.length === 0 && itemMode === 'closed' && (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No outstanding items yet.
            </CardContent>
          </Card>
        )}

        {outstanding.map((item) => (
          <OutstandingItemCard
            key={item.id}
            item={item}
            onEdit={() => setItemMode({ editing: item })}
            onDelete={() => onItemDelete(item.id)}
            isReadOnly={isReadOnly}
          />
        ))}

        {!isReadOnly && itemMode !== 'closed' && tenantId && (
          <OutstandingItemEntryForm
            dsrId={dsrId}
            tenantId={tenantId}
            editing={itemMode !== 'adding' ? itemMode.editing : undefined}
            onSaved={onItemSaved}
            onCancel={() => setItemMode('closed')}
          />
        )}

        {!isReadOnly && itemMode === 'closed' && (
          <Button
            variant="outline"
            className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
            onClick={() => setItemMode('adding')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add Outstanding Item
          </Button>
        )}
      </section>

      {/* ── Summary ── */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Next-Shift Risk Assessment
        </h3>
        {tenantId && (
          <SummaryEditor
            dsrId={dsrId}
            tenantId={tenantId}
            initial={summary}
            isReadOnly={isReadOnly}
          />
        )}
      </section>
    </div>
  );
}

// ─── Forecasted Activity ────────────────────────────────────────────────────

function ForecastedActivityCard({
  entry,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: ForecastedActivityRow;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const meta = RISK_LEVEL_OPTIONS.find((o) => o.value === entry.anticipated_risk);
  const tagCount = (entry.activity_tags ?? []).length;
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {meta && (
            <Badge style={{ backgroundColor: meta.color }} className="text-white">
              {meta.label.toUpperCase()}
            </Badge>
          )}
          {entry.is_permits_required && (
            <Badge variant="outline" className="border-mozaic-orange text-mozaic-orange">
              PERMITS REQUIRED
            </Badge>
          )}
          {tagCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {tagCount} tag{tagCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <p className="font-semibold text-mozaic-navy">{entry.description}</p>
        {entry.risk_notes && (
          <p className="mt-1 text-xs text-muted-foreground">{entry.risk_notes}</p>
        )}
        {!isReadOnly && (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ForecastedActivityEntryForm({
  dsrId,
  tenantId,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  editing?: ForecastedActivityRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ActivityFormValues>({
    resolver: zodResolver(forecastedActivitySchema),
    defaultValues: editing
      ? {
          description: editing.description,
          anticipated_risk: editing.anticipated_risk,
          risk_notes: editing.risk_notes ?? '',
          activity_tags: editing.activity_tags ?? [],
          is_permits_required: editing.is_permits_required,
          estimated_duration: editing.estimated_duration?.toString() ?? '',
          notes: editing.notes ?? '',
        }
      : EMPTY_ACTIVITY,
  });

  const risk = watch('anticipated_risk');

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveForecastedActivityRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        description: values.description,
        anticipatedRisk: values.anticipated_risk as ForecastRiskLevel,
        riskNotes: values.risk_notes || null,
        activityTags: values.activity_tags.length > 0 ? values.activity_tags : null,
        isPermitsRequired: values.is_permits_required,
        estimatedDuration: values.estimated_duration ? parseFloat(values.estimated_duration) : null,
        notes: values.notes || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save activity');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">
          {editing ? 'Edit Forecasted Activity' : 'New Forecasted Activity'}
        </h3>

        <div className="space-y-2">
          <FieldLabel required>Description</FieldLabel>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={3}
                maxLength={500}
                placeholder="What activity is planned for the next shift?"
              />
            )}
          />
          <FieldError message={errors.description?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Anticipated Risk</FieldLabel>
          <Controller
            control={control}
            name="anticipated_risk"
            render={({ field }) => (
              <ChipGroup
                options={RISK_LEVEL_OPTIONS}
                value={field.value as ForecastRiskLevel}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.anticipated_risk?.message} />
        </div>

        {risk === 'high' && (
          <div className="space-y-2">
            <FieldLabel required>Risk Notes</FieldLabel>
            <Controller
              control={control}
              name="risk_notes"
              render={({ field }) => (
                <Textarea
                  {...field}
                  value={field.value ?? ''}
                  rows={2}
                  maxLength={300}
                  placeholder="Why is this rated High risk?"
                />
              )}
            />
            <FieldError message={errors.risk_notes?.message} />
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel>Activity Tags</FieldLabel>
          <Controller
            control={control}
            name="activity_tags"
            render={({ field }) => (
              <MultiChipGroup
                options={ACTIVITY_RISK_TAGS.map((t) => ({ value: t, label: t }))}
                value={field.value}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel>Estimated Duration (hours)</FieldLabel>
            <Controller
              control={control}
              name="estimated_duration"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min={0}
                  placeholder="e.g. 4.5"
                />
              )}
            />
          </div>
          <div className="flex items-end pb-2">
            <Controller
              control={control}
              name="is_permits_required"
              render={({ field }) => (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  Permits Required
                </label>
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel>Notes</FieldLabel>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                maxLength={300}
                placeholder="Additional notes"
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Activity'
            ) : (
              'Save Activity'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Outstanding Item ───────────────────────────────────────────────────────

function OutstandingItemCard({
  item,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  item: OutstandingItemRow;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const meta = PRIORITY_OPTIONS.find((o) => o.value === item.priority);
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-center justify-between gap-3">
          {meta && (
            <Badge style={{ backgroundColor: meta.color }} className="text-white">
              {meta.label.toUpperCase()}
            </Badge>
          )}
        </div>
        <p className="text-sm text-mozaic-navy">{item.description}</p>
        {!isReadOnly && (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OutstandingItemEntryForm({
  dsrId,
  tenantId,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  editing?: OutstandingItemRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ItemFormValues>({
    resolver: zodResolver(outstandingItemSchema),
    defaultValues: editing
      ? { description: editing.description, priority: editing.priority }
      : EMPTY_ITEM,
  });

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveOutstandingItemRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        description: values.description,
        priority: values.priority as ItemPriority,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save item');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">
          {editing ? 'Edit Outstanding Item' : 'New Outstanding Item'}
        </h3>

        <div className="space-y-2">
          <FieldLabel required>Description</FieldLabel>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={2}
                maxLength={300}
                placeholder="What's the outstanding item?"
              />
            )}
          />
          <FieldError message={errors.description?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Priority</FieldLabel>
          <Controller
            control={control}
            name="priority"
            render={({ field }) => (
              <ChipGroup
                options={PRIORITY_OPTIONS}
                value={field.value as ItemPriority}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.priority?.message} />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Item'
            ) : (
              'Save Item'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Summary ────────────────────────────────────────────────────────────────

function SummaryEditor({
  dsrId,
  tenantId,
  initial,
  isReadOnly,
}: {
  dsrId: string;
  tenantId: string;
  initial: ForecastingSummaryRow | null | undefined;
  isReadOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<SummaryFormValues>({
    resolver: zodResolver(summarySchema),
    defaultValues: {
      overall_risk_assessment: 'low_risk',
      risk_comments: '',
      resource_adequacy: 'fully_adequate',
      resource_comments: '',
      next_shift_narrative: '',
    },
  });

  // Re-seed the form when the row arrives from the server.
  useEffect(() => {
    if (initial) {
      reset({
        overall_risk_assessment: initial.overall_risk_assessment,
        risk_comments: initial.risk_comments ?? '',
        resource_adequacy: initial.resource_adequacy,
        resource_comments: initial.resource_comments ?? '',
        next_shift_narrative: initial.next_shift_narrative ?? '',
      });
    }
  }, [initial, reset]);

  const overallRisk = watch('overall_risk_assessment');

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveForecastingSummary({
        dsrReportId: dsrId,
        tenantId,
        overallRiskAssessment: values.overall_risk_assessment as NextShiftRiskAssessment,
        riskComments: values.risk_comments || null,
        resourceAdequacy: values.resource_adequacy as ResourceAdequacy,
        resourceComments: values.resource_comments || null,
        nextShiftNarrative: values.next_shift_narrative || null,
      });
      queryClient.invalidateQueries({ queryKey: ['entry_forecasting_summary', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save summary');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="space-y-2">
          <FieldLabel required>Overall Risk Assessment (next shift)</FieldLabel>
          <Controller
            control={control}
            name="overall_risk_assessment"
            render={({ field }) => (
              <ChipGroup
                options={RISK_ASSESSMENT_OPTIONS}
                value={field.value as NextShiftRiskAssessment}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.overall_risk_assessment?.message} />
        </div>

        {overallRisk !== 'low_risk' && (
          <div className="space-y-2">
            <FieldLabel required>Risk Comments</FieldLabel>
            <Controller
              control={control}
              name="risk_comments"
              render={({ field }) => (
                <Textarea
                  {...field}
                  value={field.value ?? ''}
                  rows={2}
                  maxLength={300}
                  placeholder="Why is the risk elevated?"
                />
              )}
            />
            <FieldError message={errors.risk_comments?.message} />
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel required>Resource Adequacy</FieldLabel>
          <Controller
            control={control}
            name="resource_adequacy"
            render={({ field }) => (
              <ChipGroup
                options={RESOURCE_ADEQUACY_OPTIONS}
                value={field.value as ResourceAdequacy}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.resource_adequacy?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel>Resource Comments</FieldLabel>
          <Controller
            control={control}
            name="resource_comments"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={2}
                maxLength={300}
                placeholder="Anything missing or unusual about resources?"
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel>Next-Shift Narrative</FieldLabel>
          <Controller
            control={control}
            name="next_shift_narrative"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={3}
                maxLength={500}
                placeholder="Brief narrative for the next shift"
              />
            )}
          />
        </div>

        {!isReadOnly && (
          <div className="flex justify-end">
            <Button
              className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
              onClick={onSubmit}
              disabled={saving || !isDirty}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Summary'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
